
import React, { useEffect, useState } from 'react';
import { TableCell } from '@/components/ui/table';
import { Shirt } from 'lucide-react';
import { Attendee } from '@/types/attendee';
import { formatTShirtSize } from '@/utils/formatUtils';

interface TShirtCellProps {
  tShirtSize?: string;
  originalAttendee?: Attendee;
  isMobile: boolean;
}

export const TShirtCell: React.FC<TShirtCellProps> = ({ 
  tShirtSize, 
  originalAttendee, 
  isMobile 
}) => {
  // State to track the most reliable t-shirt size
  const [effectiveTShirtSize, setEffectiveTShirtSize] = useState<string>('');
  
  // Update the effective t-shirt size whenever props change
  useEffect(() => {
    // Try all possible sources in order of reliability
    const size = tShirtSize || originalAttendee?.t_shirt_size || '';
    if (size !== effectiveTShirtSize) {
      console.log(`TShirtCell updating effective size for ${originalAttendee?.code} from ${effectiveTShirtSize} to ${size}`);
      setEffectiveTShirtSize(size);
    }
  }, [tShirtSize, originalAttendee, effectiveTShirtSize]);
  
  // Format the t-shirt size for display
  const formattedSize = formatTShirtSize(effectiveTShirtSize);
  
  // Determine gender-based styling
  const isMale = formattedSize.includes('(Nam)');
  const isFemale = formattedSize.includes('(Nữ)');
  
  // Styling based on gender
  const iconColorClass = isMale 
    ? 'text-blue-500 dark:text-blue-400' 
    : isFemale 
      ? 'text-pink-500 dark:text-pink-400'
      : 'text-gray-500 dark:text-gray-400';
      
  const textColorClass = isMale 
    ? 'text-blue-700 dark:text-blue-400' 
    : isFemale 
      ? 'text-pink-700 dark:text-pink-400'
      : 'text-gray-700 dark:text-gray-400';
      
  const bgColorClass = isMale 
    ? 'bg-blue-50 dark:bg-blue-900/20' 
    : isFemale 
      ? 'bg-pink-50 dark:bg-pink-900/20'
      : 'bg-gray-50 dark:bg-gray-800/20';
  
  // Debug log to help diagnose issues
  console.log(`TShirtCell rendering for ${originalAttendee?.code}: tShirtSize=${tShirtSize}, original=${originalAttendee?.t_shirt_size}, effective=${effectiveTShirtSize}, formatted=${formattedSize}`);
  
  return (
    <TableCell className={isMobile ? 'whitespace-nowrap pl-2 pr-1 py-2' : ''}>
      {effectiveTShirtSize ? (
        <div className="flex items-center gap-1.5">
          <Shirt className={`h-4 w-4 ${iconColorClass}`} />
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${bgColorClass} ${textColorClass}`}>
            {formattedSize}
          </span>
        </div>
      ) : (
        <span className="text-xs text-gray-400 italic">N/A</span>
      )}
    </TableCell>
  );
};
